import { useState, VFC } from 'react';
import { message, Form, Space, Modal, Row } from 'antd';
import { useNavigate, useParams } from 'react-router-dom';
import { v4 } from 'uuid';
import { SaveOutlined } from '@ant-design/icons';
import { useSessionStore } from 'store/session';
import { useServices } from 'services/useServices';
import { captureException } from '@sentry/react';
import { FormattedMessage, useIntl } from 'react-intl';
import { useStore, selectInstallMeterFormData, selectInstallMeterFormPhotos } from '../../useStore';
import { useScrollTopOnMount } from '../../../../hooks/useScrollTopOnMount';
import { InstallationDescriptions } from './InstallationDescriptions';
import { Button } from '../../../../components/Button/Button';
import { PhotoDescriptions } from './PhotoDescriptions';
import { getInstallMeterDbApi } from '../../../../api/indexed_db/installMeterDb';
import { useInstallMeterStepsStore } from '../../components/store';
import { ReviewStepContent as messages } from './messages';
import { useMeterModelSuggestionsStore } from '../../../../store/useMeterModelSuggestionsStore';

export const ReviewStepContent: VFC = () => {
  const { id } = useParams();
  const { inventoryMeterInstaller } = useServices();

  const { apiKey, email } = useSessionStore();
  const { reset, setIsSendingData, isSendingData, enmonMeter, setQueuedMetersCount } = useStore();
  const { reset: resetStepsStore } = useInstallMeterStepsStore();
  const { reset: resetMeterModelSuggestionsStore } = useMeterModelSuggestionsStore();
  const formPhotos = useStore(selectInstallMeterFormPhotos);
  const intl = useIntl();

  const navigate = useNavigate();

  const forceScrollTop = useScrollTopOnMount();

  const handleOnSubmit = async () => {
    setIsSendingData(true);
    await inventoryMeterInstaller
      .install({
        data: selectInstallMeterFormData(email)(useStore.getState()),
        formPhotos,
        inventoryMeterId: parseInt(id ?? ''),
      })
      .then(() => {
        message.success(intl.formatMessage(messages.recordSentSuccess));
        reset();
        resetMeterModelSuggestionsStore();
        navigate('/');
        resetStepsStore();
      })
      .catch(error => {
        message.error(intl.formatMessage(messages.recordSentError));
        console.error(error);
        captureException(error);
      })
      .finally(() => {
        setIsSendingData(false);
        forceScrollTop();
      });
  };

  const [visible, setVisible] = useState(false);

  const handleSaveRecord = async () => {
    setVisible(false);
    const installationDb = getInstallMeterDbApi();
    await installationDb.insertData([
      {
        id: v4(),
        locationApiKey: apiKey ?? '',
        installationMeter: {
          data: selectInstallMeterFormData(email)(useStore.getState()),
          formPhotos,
          inventoryMeterId: parseInt(id ?? ''),
        },
        wasSynced: 0,
      },
    ]);
    const metersCount = await installationDb.countNotSynchronized();
    setQueuedMetersCount(metersCount);
    message.success(intl.formatMessage(messages.queuedMetersSentSuccess));
    reset();
    resetMeterModelSuggestionsStore();
    navigate('/');
    resetStepsStore();
  };

  if (!enmonMeter) {
    return null;
  }

  return (
    <>
      <Form id="reviewStepForm" layout="horizontal" name="basic" onFinish={() => setVisible(true)} />
      <Space className="w-100" direction="vertical">
        <InstallationDescriptions />
        <PhotoDescriptions enmonMeter={enmonMeter} />
      </Space>

      <Modal
        maskClosable
        confirmLoading={isSendingData}
        footer={
          <Row wrap={false}>
            <Button icon={<SaveOutlined />} loading={isSendingData} onClick={handleSaveRecord}>
              <FormattedMessage {...messages.save} />
            </Button>
            <Button
              key="submit"
              disabled={isSendingData}
              loading={isSendingData}
              type="primary"
              onClick={() => handleOnSubmit()}
            >
              {isSendingData ? <FormattedMessage {...messages.sending} /> : <FormattedMessage {...messages.send} />}
            </Button>
          </Row>
        }
        open={visible}
        onCancel={() => setVisible(false)}
      >
        <FormattedMessage {...messages.sendOptions} />
      </Modal>
    </>
  );
};
